import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  getPendingUsers,
  searchUsers,
  updateUserPermissions,
  getUsersByManager,
  getAllUsers,
  getInvitations,
  createInvitation,
  deleteInvitation,
  deleteUser
} from '../../services/firestoreService';
import { syncAllToSAP, syncMitigationToSAP, syncPipelineToSAP, type SyncAllToSAPResult } from '../../services/apiService';
import type { User, UserRole, Country, Invitation, AccessPermissions } from '../../types';
import { ArrowLeft, Search, Loader2, Save, Settings, Mail, Trash2, UserPlus, CheckCircle, Database, AlertTriangle } from 'lucide-react';

export function AdminPanel() {
  const { isManager, user: currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'users' | 'invitations' | 'data'>('users');

  // Data / SAP Sync State
  const [syncScope, setSyncScope] = useState<'all' | 'MT' | 'CA'>('all');
  // Mes a reconstruir en SAP ('YYYY-MM'); vacío = mes en curso.
  const [syncPeriod, setSyncPeriod] = useState('');
  const [syncLoading, setSyncLoading] = useState<'preview' | 'full' | null>(null);
  const [syncResult, setSyncResult] = useState<SyncAllToSAPResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Users State
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [managedUsers, setManagedUsers] = useState<User[]>([]);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [countries, setCountries] = useState<Record<string, Country>>({});
  const [regions, setRegions] = useState<Record<string, { id: string; name: string; countries: Record<string, Country> }>>({});

  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Shared Edit/Invite State
  const [selectedRole, setSelectedRole] = useState<UserRole>('commercial');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);

  // Invitations State
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  // Country Search State
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  // Redirigir si no es manager o regional
  useEffect(() => {
    if (!authLoading && !isManager && currentUser?.role !== 'regional') {
      navigate('/');
    }
  }, [isManager, currentUser, authLoading, navigate]);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        const [pending, structureDoc, invites] = await Promise.all([
          getPendingUsers(),
          (async () => {
            const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
            const { db } = await import('../../config/firebase');
            const structureRef = firestoreDoc(db, 'structure', 'main');
            const structureSnapshot = await getDoc(structureRef);
            return structureSnapshot.exists() ? structureSnapshot.data() : null;
          })(),
          isManager ? getInvitations() : Promise.resolve([])
        ]);

        setPendingUsers(pending);
        setInvitations(invites);

        if (structureDoc?.regions) {
          setRegions(structureDoc.regions);
        }

        // Si es Regional, filtrar por regiones
        if (currentUser?.role === 'regional' && currentUser?.access_permissions?.regions) {
          const userRegions = currentUser.access_permissions.regions;
          const filteredCountries: Record<string, Country> = {};

          // Obtener todos los países de las regiones asignadas
          userRegions.forEach(regionId => {
            if (structureDoc?.regions?.[regionId]?.countries) {
              Object.assign(filteredCountries, structureDoc.regions[regionId].countries);
            }
          });

          setCountries(filteredCountries);

          // Cargar usuarios gestionados (solo comerciales)
          const users = await getUsersByManager([], userRegions);
          setManagedUsers(users.filter(u => u.uid !== currentUser.uid && u.role === 'commercial'));
        }
        // Si es Manager, mostrar TODOS los países (regions: null significa acceso total)
        else if (currentUser?.role === 'manager') {
          const allCountries: Record<string, Country> = {};

          // Aplanar la estructura de regiones para obtener TODOS los países
          if (structureDoc?.regions) {
            Object.values(structureDoc.regions).forEach((region: any) => {
              if (region.countries) {
                Object.assign(allCountries, region.countries);
              }
            });
          }

          setCountries(allCountries);

          const users = await getAllUsers();
          setManagedUsers(users.filter(u => u.uid !== currentUser.uid));
        } else {
          setCountries({});
          setManagedUsers([]);
        }

      } catch (error) {
        console.error('Error loading admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadInitialData();
    }
  }, [currentUser, isManager]);

  // Buscar usuarios
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const results = await searchUsers(searchTerm);

      // Filtrar usuarios
      const managerCountries = currentUser?.access_permissions?.countries || [];

      const filteredResults = results.filter(user => {
        if (user.uid === currentUser?.uid) return true;
        if (!user.access_permissions?.countries) return true;

        // Si es Manager, ve a todos
        if (currentUser?.role === 'manager') return true;

        const userCountries = user.access_permissions.countries;
        return userCountries.some(countryId => managerCountries.includes(countryId));
      });

      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  };

  // Iniciar edición de usuario
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    const role = user.role || 'commercial';
    setSelectedRole(role);
    if (role === 'manager' || role === 'innovation' || role === 'innovation++') {
      setSelectedRegions([]);
      setSelectedCountries([]);
      setSelectedClients(null);
    } else {
      setSelectedRegions(user.access_permissions?.regions || []);
      setSelectedCountries(user.access_permissions?.countries || []);
      setSelectedClients(user.access_permissions?.clients || null);
    }
  };

  // Cancelar edición/invitación
  const handleCancel = () => {
    setEditingUser(null);
    setIsInviting(false);
    setInviteEmail('');
    setSelectedRole('commercial');
    setSelectedRegions([]);
    setSelectedCountries([]);
    setSelectedClients(null);
    setCountrySearchTerm('');
  };

  // Handle role change
  const handleRoleChange = (newRole: UserRole) => {
    setSelectedRole(newRole);
    if (newRole === 'manager' || newRole === 'innovation' || newRole === 'innovation++') {
      setSelectedRegions([]);
      setSelectedCountries([]);
      setSelectedClients(null);
    }
  };

  // Helper para construir objeto de permisos limpio (sin undefined)
  const buildPermissionsObject = (): AccessPermissions | undefined => {
    if (selectedRole === 'manager' || selectedRole === 'innovation' || selectedRole === 'innovation++') return undefined;

    const permissions: AccessPermissions = {
      countries: selectedCountries,
      clients: selectedClients,
    };

    if (selectedRole === 'regional') {
      permissions.regions = selectedRegions;
    }

    return permissions;
  };

  // Guardar cambios usuario existente
  const handleSaveUser = async () => {
    if (!editingUser) return;

    // Validación
    if (!validatePermissions()) return;

    try {
      setSaving(true);
      const permissions = buildPermissionsObject();

      await updateUserPermissions({
        uid: editingUser.uid,
        role: selectedRole,
        access_permissions: permissions,
      });

      const updatedUser = {
        ...editingUser,
        role: selectedRole,
        access_permissions: permissions,
      };

      // Actualizar lista local
      // 1. Si estaba en pending, lo quitamos
      setPendingUsers((prev) => prev.filter((u) => u.uid !== editingUser.uid));

      // 2. Si estaba en search results, lo actualizamos
      setSearchResults((prev) =>
        prev.map((u) =>
          u.uid === editingUser.uid ? updatedUser : u
        )
      );

      // 3. Actualizamos managedUsers (My Team)
      setManagedUsers((prev) => {
        const exists = prev.some(u => u.uid === editingUser.uid);
        if (exists) {
          // Si ya existía, actualizamos sus datos
          return prev.map((u) => u.uid === editingUser.uid ? updatedUser : u);
        } else {
          // Si no existía (ej: venía de pending), lo añadimos a la lista si corresponde
          // Nota: Si hay filtros activos en managedUsers (como Regional solo viendo Commercials), 
          // aquí asumimos que el admin quiere ver el resultado de su acción.
          return [updatedUser, ...prev];
        }
      });

      handleCancel();
      alert('User permissions updated successfully');
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error updating user permissions');
    } finally {
      setSaving(false);
    }
  };


  // Crear invitación
  const handleCreateInvitation = async () => {
    if (!inviteEmail) return;
    if (!validatePermissions()) return;

    try {
      setSaving(true);
      const permissions = buildPermissionsObject();

      const invitationData: Partial<Invitation> = {
        email: inviteEmail,
        role: selectedRole,
        createdBy: currentUser?.uid || 'unknown'
      };

      // Solo añadimos access_permissions si no es undefined
      if (permissions) {
        invitationData.access_permissions = permissions;
      }

      await createInvitation(invitationData);

      // Recargar invitaciones
      const updatedInvites = await getInvitations();
      setInvitations(updatedInvites);

      handleCancel();
      // Updated alert since email sending is removed
      alert('Invitation created successfully.');
    } catch (error) {
      console.error('Error creating invitation:', error);
      alert('Error creating invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInvitation = async (id: string) => {
    if (!confirm("Are you sure you want to delete this invitation?")) return;
    try {
      await deleteInvitation(id);
      setInvitations(prev => prev.filter(i => i.id !== id));
    } catch (error) {
      console.error(error);
      alert("Error deleting invitation");
    }
  }

  const handleDeleteUser = async (uid: string, email: string) => {
    if (!confirm(`Are you sure you want to delete user ${email}? This will revoke their access immediately.`)) return;

    try {
      await deleteUser(uid);

      // Update local state
      setPendingUsers(prev => prev.filter(u => u.uid !== uid));
      setManagedUsers(prev => prev.filter(u => u.uid !== uid));
      setSearchResults(prev => prev.filter(u => u.uid !== uid));

      alert("User deleted successfully");
    } catch (error) {
      console.error('Error deleting user:', error);
      alert("Error deleting user");
    }
  };

  // Sincronización masiva Firestore -> SAP (todo, solo Mitigation o solo pipeline/Commercial Actions)
  const handleSyncAll = async (dryRun: boolean) => {
    const scopeLabel = syncScope === 'MT' ? 'Mitigation' : syncScope === 'CA' ? 'Commercial Actions (pipeline)' : 'Commercial Actions + Mitigation';

    if (!dryRun) {
      const confirmed = confirm(
        `Esto enviará TODAS las acciones de ${scopeLabel} de Firestore a SAP PRODUCTIVO ahora mismo ` +
        `(snapshot de ${syncPeriod || 'el mes en curso'}). ` +
        'No es reversible desde aquí. ¿Seguro que quieres continuar?'
      );
      if (!confirmed) return;
    }

    setSyncLoading(dryRun ? 'preview' : 'full');
    setSyncError(null);
    setSyncResult(null);

    try {
      const period = syncPeriod || undefined;
      const result = syncScope === 'MT'
        ? await syncMitigationToSAP(dryRun, false, period)
        : syncScope === 'CA'
          ? await syncPipelineToSAP(dryRun, false, period)
          : await syncAllToSAP(dryRun, false, undefined, period);
      setSyncResult(result);
    } catch (error) {
      console.error('Error en sincronización con SAP:', error);
      setSyncError(error instanceof Error ? error.message : 'Error desconocido al sincronizar con SAP');
    } finally {
      setSyncLoading(null);
    }
  };

  const validatePermissions = () => {
    if (selectedRole === 'manager' || selectedRole === 'innovation' || selectedRole === 'innovation++') {
      return true;
    }
    if (selectedRole === 'regional' && selectedRegions.length === 0) {
      alert('Please select at least one region for Regional users');
      return false;
    }
    if (selectedRole === 'commercial' && selectedCountries.length === 0) {
      alert('Please select at least one country for Commercial users');
      return false;
    }
    return true;
  };

  // Toggle helpers
  const toggleRegion = (regionId: string) => {
    setSelectedRegions((prev) => {
      const newRegions = prev.includes(regionId)
        ? prev.filter((r) => r !== regionId)
        : [...prev, regionId];

      if (selectedRole === 'regional') {
        const newCountries: string[] = [];
        newRegions.forEach(rId => {
          if (regions[rId]?.countries) {
            Object.keys(regions[rId].countries).forEach(cId => {
              if (!newCountries.includes(cId)) newCountries.push(cId);
            });
          }
        });
        setSelectedCountries(newCountries);
      }
      return newRegions;
    });
  };

  const toggleCountry = (countryId: string) => {
    setSelectedCountries((prev) =>
      prev.includes(countryId)
        ? prev.filter((c) => c !== countryId)
        : [...prev, countryId]
    );
  };

  // Modal Content (Shared between Edit and Invite)
  const renderPermissionForm = () => (
    <div className="space-y-4">
      {/* Role Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Role
        </label>
        <select
          value={selectedRole}
          onChange={(e) => handleRoleChange(e.target.value as UserRole)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="commercial">Commercial</option>
          {currentUser?.role === 'manager' && (
            <>
              <option value="regional">Regional</option>
              <option value="innovation">Innovation</option>
              <option value="innovation++">Innovation++</option>
              <option value="manager">Manager</option>
            </>
          )}
        </select>
        {selectedRole === 'manager' && (
          <p className="text-sm text-[#eb4a4a] mt-1">
            Manager role has full access to all regions and countries
          </p>
        )}
        {selectedRole === 'innovation' && (
          <p className="text-sm text-[#eb4a4a] mt-1">
            Innovation role has access to Innovation Team (Commercial Actions hidden)
          </p>
        )}
        {selectedRole === 'innovation++' && (
          <p className="text-sm text-[#eb4a4a] mt-1">
            Innovation++ role has full visibility (Manager + Innovation) but read-only access (no edits)
          </p>
        )}
      </div>

      {/* Region Selection */}
      {selectedRole === 'regional' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Regions (select at least one)
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
            {Object.entries(regions).map(([id, region]) => (
              <label key={id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRegions.includes(id)}
                  onChange={() => toggleRegion(id)}
                  className="rounded border-gray-300 text-[#eb4a4a] focus:ring-[#eb4a4a]"
                />
                <span className="text-sm text-gray-700">{region.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Country Selection */}
      {selectedRole === 'commercial' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Countries (select at least one)
          </label>
          {/* Country Search Input */}
          <div className="mb-2 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search country..."
              value={countrySearchTerm}
              onChange={(e) => setCountrySearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-[#eb4a4a] focus:border-[#eb4a4a]"
            />
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
            {Object.entries(countries)
              .filter(([_, country]) =>
                country.name.toLowerCase().includes(countrySearchTerm.toLowerCase())
              )
              .map(([id, country]) => (
                <label key={id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCountries.includes(id)}
                    onChange={() => toggleCountry(id)}
                    className="rounded border-gray-300 text-[#eb4a4a] focus:ring-[#eb4a4a]"
                  />
                  <span className="text-sm text-gray-700">{country.name}</span>
                </label>
              ))}
            {Object.entries(countries).filter(([_, country]) =>
              country.name.toLowerCase().includes(countrySearchTerm.toLowerCase())
            ).length === 0 && (
                <p className="text-sm text-gray-500 text-center py-2">No countries found</p>
              )}
          </div>
        </div>
      )}

      {/* Client Restriction */}
      {selectedRole === 'commercial' && (
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={selectedClients !== null}
              onChange={(e) => setSelectedClients(e.target.checked ? [] : null)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Restrict to specific clients (leave unchecked for all clients)
            </span>
          </label>

          {selectedClients !== null && (
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 ml-6">
              {selectedCountries.map(countryId => {
                const country = countries[countryId];
                if (!country || !country.clients) return null;

                return (
                  <div key={countryId} className="mb-2">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-1">{country.name}</p>
                    {Object.values(country.clients).map(client => (
                      <label key={client.id} className="flex items-center gap-2 cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => {
                            setSelectedClients(prev =>
                              prev?.includes(client.id)
                                ? prev.filter(c => c !== client.id)
                                : [...(prev || []), client.id]
                            );
                          }}
                          className="rounded border-gray-300 text-[#eb4a4a] focus:ring-[#eb4a4a]"
                        />
                        <span className="text-sm text-gray-700">{client.name}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
              {selectedCountries.length === 0 && (
                <p className="text-sm text-gray-500 italic">Select a country first</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#eb4a4a] shadow-md">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 text-white hover:bg-white/10 rounded-md transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              <p className="text-sm text-white/90">Manage users and invitations</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${activeTab === 'users'
              ? 'border-b-2 border-[#eb4a4a] text-[#eb4a4a]'
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`px-4 py-2 font-medium text-sm focus:outline-none flex items-center gap-2 ${activeTab === 'invitations'
              ? 'border-b-2 border-[#eb4a4a] text-[#eb4a4a]'
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            Invitations
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {invitations.length}
            </span>
          </button>
          {isManager && (
            <button
              onClick={() => setActiveTab('data')}
              className={`px-4 py-2 font-medium text-sm focus:outline-none flex items-center gap-2 ${activeTab === 'data'
                ? 'border-b-2 border-[#eb4a4a] text-[#eb4a4a]'
                : 'text-gray-500 hover:text-gray-700'}`}
            >
              Data
            </button>
          )}
        </div>

        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Search Users</h2>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by email or name..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-[#eb4a4a] focus:border-[#eb4a4a]"
                />
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="px-6 py-2 bg-[#eb4a4a] text-white rounded-md hover:bg-[#d43b3b] disabled:opacity-50"
                >
                  {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="mt-4 space-y-2">
                  {searchResults.map((user) => (
                    <div key={user.uid} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div>
                        <p className="font-medium text-gray-900">{user.email}</p>
                        <p className="text-sm text-gray-600">Role: {user.role || 'Not assigned'}</p>
                      </div>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="px-3 py-1 text-sm bg-[#eb4a4a] text-white rounded-md hover:bg-[#d43b3b]"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.uid, user.email)}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 ml-2"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Users */}
            {pendingUsers.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Pending Approvals ({pendingUsers.length})
                </h2>
                <div className="space-y-2">
                  {pendingUsers.map((user) => (
                    <div key={user.uid} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div>
                        <p className="font-medium text-gray-900">{user.email}</p>
                        <p className="text-sm text-gray-600">{user.displayName || 'No name provided'}</p>
                      </div>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Approve
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Managed Users List */}
            {!searching && managedUsers.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">My Team</h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {managedUsers.map((user) => (
                    <div key={user.uid} className="flex flex-col p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-lg overflow-hidden border border-gray-200">
                            {user.photoURL ? (
                              <img src={user.photoURL} alt={user.displayName} className="w-full h-full object-cover" />
                            ) : (
                              (user.displayName || user.email).charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate max-w-[150px]" title={user.displayName || user.email}>
                              {user.displayName || 'No Name'}
                            </p>
                            <p className="text-xs text-gray-500 truncate max-w-[150px]" title={user.email}>
                              {user.email}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            user.role === 'manager'
                              ? 'bg-purple-100 text-purple-700'
                              : user.role === 'regional'
                                ? 'bg-blue-100 text-blue-700'
                                : user.role === 'innovation' || user.role === 'innovation++'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'No Role'}
                        </span>
                      </div>
                      <div className="mt-auto pt-3 border-t border-gray-100 flex justify-end">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-sm font-medium text-[#eb4a4a] hover:text-[#d43b3b] transition-colors flex items-center gap-1"
                        >
                          <Settings className="w-3 h-3" />
                          Edit Permissions
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.uid, user.email)}
                          className="text-sm font-medium text-red-600 hover:text-red-900 transition-colors flex items-center gap-1 ml-4"
                          title="Delete Users"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'invitations' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Invitations</h2>
              <button
                onClick={() => setIsInviting(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#eb4a4a] text-white rounded-md hover:bg-[#d43b3b] shadow-sm"
              >
                <UserPlus className="w-4 h-4" />
                Invite User
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invitations.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                          No pending invitations found.
                        </td>
                      </tr>
                    ) : (
                      invitations.map((invite) => (
                        <tr key={invite.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invite.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{invite.role}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${invite.status === 'used' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {invite.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {invite.createdAt?.toDate().toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() => handleDeleteInvitation(invite.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Invitation"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'data' && isManager && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold text-gray-900">Sincronización masiva con SAP</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Envía las acciones de Firestore a SAP (webservice
                <code className="mx-1 px-1 py-0.5 bg-gray-100 rounded">ZCAT_FIRESTORE_TO_SAP</code>).
                Usa primero la vista previa para comprobar qué se enviaría, sin escribir nada.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Alcance</label>
                <select
                  value={syncScope}
                  onChange={(e) => setSyncScope(e.target.value as 'all' | 'MT' | 'CA')}
                  disabled={syncLoading !== null}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">Todo (Commercial Actions + Mitigation)</option>
                  <option value="MT">Solo Mitigation</option>
                  <option value="CA">Solo Commercial Actions (pipeline)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mes a reconstruir en SAP</label>
                <input
                  type="month"
                  value={syncPeriod}
                  max={new Date().toISOString().slice(0, 7)}
                  onChange={(e) => setSyncPeriod(e.target.value)}
                  disabled={syncLoading !== null}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Vacío = mes en curso. Un mes anterior regenera su snapshot en SAP con los datos actuales de Firestore.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleSyncAll(true)}
                  disabled={syncLoading !== null}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {syncLoading === 'preview' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Vista previa (Dry Run)
                </button>
                <button
                  onClick={() => handleSyncAll(false)}
                  disabled={syncLoading !== null}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {syncLoading === 'full' ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                  Sincronización definitiva (SAP productivo)
                </button>
              </div>

              {syncError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {syncError}
                </div>
              )}

              {syncResult && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-sm font-medium text-gray-900 mb-2">{syncResult.message}</p>
                  <p className="text-sm text-gray-600 mb-2">
                    Total: {syncResult.count}
                    {syncResult.by_table && ` (CA=${syncResult.by_table.CA}, MT=${syncResult.by_table.MT})`}
                    {typeof syncResult.updated === 'number' && ` · Actualizados en Firestore: ${syncResult.updated}`}
                    {typeof syncResult.failed === 'number' && syncResult.failed > 0 && ` · Pendientes por error: ${syncResult.failed}`}
                  </p>
                  {syncResult.dry_run && syncResult.payload_preview && (
                    <pre className="text-xs bg-white border border-gray-200 rounded-md p-3 overflow-x-auto max-h-96">
                      {JSON.stringify(syncResult.payload_preview, null, 2)}
                    </pre>
                  )}
                  {syncResult.skipped && syncResult.skipped.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-amber-700 mb-1">
                        Documentos NO enviados a SAP ({syncResult.skipped.length}): corrige el país/estructura y vuelve a sincronizar.
                      </p>
                      <pre className="text-xs bg-amber-50 border border-amber-200 rounded-md p-3 overflow-x-auto max-h-96">
                        {JSON.stringify(syncResult.skipped, null, 2)}
                      </pre>
                    </div>
                  )}
                  {syncResult.chunk_errors && syncResult.chunk_errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-red-700 mb-1">Chunks con error (quedan pendientes, se reintentarán en el próximo resync):</p>
                      <pre className="text-xs bg-red-50 border border-red-200 rounded-md p-3 overflow-x-auto max-h-96">
                        {JSON.stringify(syncResult.chunk_errors, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Edit User Permissions</h3>
                <div className="p-3 bg-gray-50 rounded-md mb-4">
                  <p className="font-medium text-gray-900">{editingUser.email}</p>
                  <p className="text-sm text-gray-600">{editingUser.displayName}</p>
                </div>
                {renderPermissionForm()}
                <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t">
                  <button onClick={handleCancel} disabled={saving} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button onClick={handleSaveUser} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#eb4a4a] text-white rounded-md hover:bg-[#d43b3b]">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Invitation Modal */}
        {isInviting && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Invite New User</h3>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="user@company.com"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#eb4a4a] focus:border-[#eb4a4a]"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">The user will be able to register with this email and will automatically receive the configured permissions.</p>
                </div>

                {renderPermissionForm()}

                <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t">
                  <button onClick={handleCancel} disabled={saving} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button
                    onClick={handleCreateInvitation}
                    disabled={saving || !inviteEmail}
                    className="flex items-center gap-2 px-4 py-2 bg-[#eb4a4a] text-white rounded-md hover:bg-[#d43b3b] disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    <span>Create Invitation</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
